<template>
  <div class="onboarding-page">
    <Card class="onboarding-page__shell" size="lg" flush>
      <div class="onboarding-page__layout">
        <div class="onboarding-page__intro">
          <button
            v-if="currentStep > 0"
            type="button"
            class="onboarding-page__back"
            @click="goBack"
          >
            <BaseIcon name="arrow" size="12" rotate="180" />
            {{ $t('onboarding.actions.back') }}
          </button>

          <h1 class="onboarding-page__title">{{ currentStepMeta.title }}</h1>
          <p class="onboarding-page__description">{{ currentStepMeta.description }}</p>

          <div class="onboarding-page__nav">
            <Btn
              variant="primary"
              :text="isLastStep ? $t('onboarding.actions.finish') : $t('onboarding.actions.next')"
              :loading="isSaving"
              class="onboarding-page__next-btn"
              @click="handleNext"
            />
            <Btn
              v-if="canSkip"
              variant="plain"
              :text="$t('onboarding.actions.skip')"
              @click="handleSkip"
            />
          </div>

          <div class="onboarding-page__dots">
            <span
              v-for="step in 4"
              :key="step"
              class="onboarding-page__dot"
              :class="{ 'onboarding-page__dot--active': step - 1 === currentStep }"
            />
          </div>
        </div>

        <div class="onboarding-page__form-panel">
          <!-- Step 1: Company -->
          <div v-if="currentStep === 0" class="onboarding-page__form">
          <TextInput
            id="onboarding-name"
            :label="$t('onboarding.fields.company_name')"
            v-model="form.name"
            :rules="['required']"
          />
          <FileInput
            :label="$t('onboarding.fields.logo')"
            accept="image/*"
            v-model="form.logo"
            :is-uploading="isLogoUploading"
            :upload-config="logoUploadConfig"
          />
          <div class="onboarding-page__row">
            <TextInput
              id="onboarding-street"
              :label="$t('onboarding.fields.street')"
              v-model="form.street_address"
            />
            <TextInput
              id="onboarding-postal"
              :label="$t('onboarding.fields.postal_code')"
              v-model="form.postal_code"
            />
          </div>
          <div class="onboarding-page__row">
            <TextInput
              id="onboarding-city"
              :label="$t('onboarding.fields.city')"
              v-model="form.city"
            />
            <TextInput
              id="onboarding-phone"
              :label="$t('onboarding.fields.phone')"
              v-model="form.phone"
            />
          </div>
          <div class="onboarding-page__row">
            <TextInput
              id="onboarding-kvk"
              :label="$t('onboarding.fields.kvk')"
              v-model="form.registration_number"
            />
            <TextInput
              id="onboarding-vat"
              :label="$t('onboarding.fields.vat')"
              v-model="form.vat_number"
            />
          </div>
          <TextInput
            id="onboarding-iban"
            :label="$t('onboarding.fields.iban')"
            v-model="form.bank_account"
            placeholder="NL00BANK0000000000"
          />
          <div class="onboarding-page__upload">
            <label class="onboarding-page__upload-label">
              {{ $t('onboarding.fields.terms_pdf') }}
            </label>
            <input
              ref="termsInputRef"
              type="file"
              accept="application/pdf,.pdf"
              class="onboarding-page__file-input"
              @change="handleTermsUpload"
            />
            <Btn
              variant="outlined"
              icon="upload"
              :text="termsFileName || $t('onboarding.actions.upload_pdf')"
              :loading="isTermsUploading"
              @click="termsInputRef?.click()"
            />
          </div>
        </div>

        <!-- Step 2: Branding -->
        <div v-else-if="currentStep === 1" class="onboarding-page__form">
          <ColorPicker
            :label="$t('onboarding.fields.primary_color')"
            v-model="form.primary_color"
          />
          <ColorPicker
            :label="$t('onboarding.fields.accent_color')"
            v-model="form.primary_accent_color"
          />
          <TextInput
            id="onboarding-reply-to"
            :label="$t('onboarding.fields.reply_to_email')"
            v-model="form.reply_to_email"
            type="email"
            placeholder="info@jouwbedrijf.nl"
          />
        </div>

        <!-- Step 3: Products -->
        <div v-else-if="currentStep === 2" class="onboarding-page__form">
          <p class="onboarding-page__hint">{{ $t('onboarding.products.hint') }}</p>
          <div v-if="productsLoading" class="onboarding-page__loading">
            <LoadingSpinner />
          </div>
          <div v-else class="onboarding-page__product-grid">
            <Card
              v-for="product in seedProducts"
              :key="product.id"
              size="sm"
              class="onboarding-page__product-card"
              :class="{
                'onboarding-page__product-card--selected': selectedProductIds.includes(product.id),
              }"
            >
              <label class="onboarding-page__product-label">
                <input
                  type="checkbox"
                  :value="product.id"
                  v-model="selectedProductIds"
                  class="onboarding-page__product-checkbox"
                />
                <img
                  v-if="product.image_url"
                  :src="product.image_url"
                  :alt="product.title"
                  class="onboarding-page__product-image"
                />
                <div v-else class="onboarding-page__product-placeholder">
                  <BaseIcon name="package" size="24" />
                </div>
                <span class="onboarding-page__product-title">{{ product.title }}</span>
              </label>
            </Card>
          </div>
        </div>

        <!-- Step 4: Team -->
        <div v-else class="onboarding-page__form">
          <TagInput
            id="onboarding-team-emails"
            :label="$t('onboarding.fields.team_emails')"
            v-model="teamInviteEmails"
            type="email"
            :placeholder="$t('onboarding.team.emails_placeholder')"
          />
          <p class="onboarding-page__help">{{ $t('onboarding.team.emails_help') }}</p>
          <SelectInput
            id="onboarding-team-role"
            :label="$t('onboarding.fields.role')"
            v-model="teamDefaultRole"
            :options="roleOptions"
          />
          <Card size="sm" class="onboarding-page__role-help">
            <p><strong>Admin:</strong> {{ $t('onboarding.team.role_admin') }}</p>
            <p>
              <strong>{{ $t('onboarding.team.role_member_label') }}:</strong>
              {{ $t('onboarding.team.role_member') }}
            </p>
            <p>
              <strong>{{ $t('onboarding.team.role_installer_label') }}:</strong>
              {{ $t('onboarding.team.role_installer') }}
            </p>
          </Card>
        </div>
        </div>
      </div>
    </Card>
  </div>
</template>

<script>
  import { ref, computed, onMounted, getCurrentInstance } from 'vue';
  import { useRouter } from 'vue-router';
  import { useUserStore } from '@/store/userStore';
  import { useToastStore } from '@/store/toastStore';
  import { useOnboarding } from '@/composables/useOnboarding';
  import { onboardingService } from '@/services/onboardingService';
  import OrganizationService from '@/services/organizationService';
  import FileService from '@/services/fileService';
  import TextInput from '@/components/inputs/TextInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import FileInput from '@/components/inputs/FileInput.vue';
  import ColorPicker from '@/components/inputs/ColorPicker.vue';
  import TagInput from '@/components/inputs/TagInput.vue';
  import Btn from '@/components/partials/Btn.vue';
  import Card from '@/components/partials/Card.vue';
  import BaseIcon from '@/components/partials/BaseIcon.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';

  const STEPS = ['company', 'branding', 'products', 'team'];

  export default {
    name: 'OnboardingPage',

    components: {
      TextInput,
      SelectInput,
      FileInput,
      ColorPicker,
      TagInput,
      Btn,
      Card,
      BaseIcon,
      LoadingSpinner,
    },

    setup() {
      const router = useRouter();
      const userStore = useUserStore();
      const toastStore = useToastStore();
      const { completeIntake } = useOnboarding();
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const organizationService = new OrganizationService();
      const currentStep = ref(0);
      const isSaving = ref(false);
      const isLogoUploading = ref(false);
      const isTermsUploading = ref(false);
      const termsInputRef = ref(null);
      const termsFileName = ref('');
      const termsPdfPath = ref('');
      const termsPublicUrl = ref('');
      const seedProducts = ref([]);
      const productsLoading = ref(false);
      const selectedProductIds = ref([]);
      const teamInviteEmails = ref('');
      const teamDefaultRole = ref('member');
      const skipped = ref({ branding: false, products: false, team: false });

      const org = userStore.currentOrganization || {};

      const form = ref({
        name: org.name || '',
        logo: org.brand_settings?.logo || '',
        street_address: org.address?.street_address || '',
        postal_code: org.address?.postal_code || '',
        city: org.address?.city || '',
        phone: org.address?.phone || '',
        registration_number: org.address?.registration_number || '',
        vat_number: org.address?.vat_number || '',
        bank_account: org.bank_account || '',
        primary_color: org.brand_settings?.primary_color || '#3b82f6',
        primary_accent_color: org.brand_settings?.primary_accent_color || '#FFFFFF',
        reply_to_email: org.crm_settings?.reply_to_email || '',
      });

      const logoUploadConfig = computed(() => ({
        type: 'logo',
        organizationId: org.id,
        context: 'organization',
      }));

      const roleOptions = computed(() => [
        { value: 'admin', label: 'Admin' },
        { value: 'member', label: $t('onboarding.team.role_member_label') },
        { value: 'installer', label: $t('onboarding.team.role_installer_label') },
      ]);

      const stepMeta = computed(() => [
        {
          title: $t('onboarding.steps.company.title'),
          description: $t('onboarding.steps.company.description'),
        },
        {
          title: $t('onboarding.steps.branding.title'),
          description: $t('onboarding.steps.branding.description'),
        },
        {
          title: $t('onboarding.steps.products.title'),
          description: $t('onboarding.steps.products.description'),
        },
        {
          title: $t('onboarding.steps.team.title'),
          description: $t('onboarding.steps.team.description'),
        },
      ]);

      const currentStepMeta = computed(() => stepMeta.value[currentStep.value] || stepMeta.value[0]);
      const isLastStep = computed(() => currentStep.value === STEPS.length - 1);
      const canSkip = computed(() => currentStep.value >= 1);

      const loadSeedProducts = async () => {
        productsLoading.value = true;
        try {
          seedProducts.value = await onboardingService.getSeedProducts();
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('onboarding.errors.load_products'),
          });
        } finally {
          productsLoading.value = false;
        }
      };

      onMounted(() => {
        loadSeedProducts();
      });

      const saveCompanyStep = async () => {
        if (!form.value.name?.trim()) {
          toastStore.addToast({
            type: 'error',
            message: $t('onboarding.errors.company_name_required'),
          });
          return false;
        }

        const payload = {
          name: form.value.name,
          address: {
            street_address: form.value.street_address,
            postal_code: form.value.postal_code,
            city: form.value.city,
            phone: form.value.phone,
            registration_number: form.value.registration_number,
            vat_number: form.value.vat_number,
          },
          bank_account: form.value.bank_account,
          brand_settings: {
            logo: form.value.logo,
          },
        };

        if (termsPdfPath.value || termsPublicUrl.value) {
          payload.settings = {
            terms_pdf_url: termsPdfPath.value || undefined,
            terms_url: termsPublicUrl.value || undefined,
          };
        }

        await userStore.updateOrganization(org.id, payload);
        return true;
      };

      const saveBrandingStep = async () => {
        await userStore.updateOrganization(org.id, {
          brand_settings: {
            primary_color: form.value.primary_color,
            primary_accent_color: form.value.primary_accent_color,
          },
          crm_settings: {
            reply_to_email: form.value.reply_to_email,
          },
        });
        return true;
      };

      const saveProductsStep = async () => {
        if (selectedProductIds.value.length === 0) {
          skipped.value.products = true;
          return true;
        }
        await onboardingService.seedProducts(selectedProductIds.value);
        return true;
      };

      const saveTeamStep = async () => {
        const emails = teamInviteEmails.value
          .split(',')
          .map((email) => email.trim())
          .filter(Boolean);

        if (emails.length === 0) {
          skipped.value.team = true;
          return true;
        }

        for (const email of emails) {
          await organizationService.inviteMember({
            email,
            role: teamDefaultRole.value,
            organization_id: org.id,
          });
        }
        return true;
      };

      const handleTermsUpload = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
          toastStore.addToast({
            type: 'error',
            message: $t('onboarding.errors.pdf_only'),
          });
          return;
        }

        isTermsUploading.value = true;
        try {
          const uploaded = await FileService.uploadFile(file, {
            type: 'proposal_attachment',
            organizationId: org.id,
            context: 'organization',
          });
          termsFileName.value = uploaded.original_name || file.name;
          termsPdfPath.value = uploaded.file_name || uploaded.url;
          termsPublicUrl.value = uploaded.url;
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('onboarding.errors.upload_failed'),
          });
        } finally {
          isTermsUploading.value = false;
        }
      };

      const handleNext = async () => {
        isSaving.value = true;
        try {
          let ok = true;
          if (currentStep.value === 0) ok = await saveCompanyStep();
          else if (currentStep.value === 1) ok = await saveBrandingStep();
          else if (currentStep.value === 2) ok = await saveProductsStep();
          else if (currentStep.value === 3) ok = await saveTeamStep();

          if (!ok) return;

          if (isLastStep.value) {
            await completeIntake(skipped.value);
            router.replace('/all-flows');
            const { showOnboardingChecklistIfNeeded } = await import(
              '@/utils/showOnboardingChecklist'
            );
            await showOnboardingChecklistIfNeeded();
            return;
          }

          currentStep.value += 1;
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('onboarding.errors.save_failed'),
          });
        } finally {
          isSaving.value = false;
        }
      };

      const handleSkip = async () => {
        if (currentStep.value === 1) skipped.value.branding = true;
        if (currentStep.value === 2) {
          skipped.value.products = true;
          currentStep.value += 1;
          return;
        }
        if (currentStep.value === 3) {
          skipped.value.team = true;
          await handleNext();
          return;
        }
        currentStep.value += 1;
      };

      const goBack = () => {
        if (currentStep.value > 0) currentStep.value -= 1;
      };

      return {
        currentStep,
        currentStepMeta,
        isLastStep,
        canSkip,
        form,
        isSaving,
        isLogoUploading,
        isTermsUploading,
        termsInputRef,
        termsFileName,
        logoUploadConfig,
        seedProducts,
        productsLoading,
        selectedProductIds,
        teamInviteEmails,
        teamDefaultRole,
        roleOptions,
        handleNext,
        handleSkip,
        goBack,
        handleTermsUpload,
      };
    },
  };
</script>

<style scoped lang="scss">
  .onboarding-page {
    min-height: 100vh;
    background: var(--slate-50);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;

    &__shell {
      max-width: 1100px;
      width: 100%;
      min-height: 600px;
    }

    &__layout {
      display: grid;
      grid-template-columns: 1fr 1.2fr;
      min-height: 600px;

      @include lg-down {
        grid-template-columns: 1fr;
      }
    }

    &__intro {
      padding: 3rem;
      display: flex;
      flex-direction: column;
      background: var(--slate-50);
      border-right: 1px solid var(--slate-200);

      @include lg-down {
        border-right: none;
        border-bottom: 1px solid var(--slate-200);
      }
    }

    &__back {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      background: none;
      border: none;
      color: var(--slate-500);
      cursor: pointer;
      padding: 0;
      margin-bottom: 1.5rem;
      @include fontSize('sm');

      @include hover {
        color: var(--slate-700);
      }
    }

    &__title {
      @include fontSize('lg');
      font-weight: 700;
      margin-bottom: 0.75rem;
      color: var(--slate-900);
    }

    &__description {
      color: var(--slate-500);
      margin-bottom: 1.5rem;
      line-height: 1.5;
    }

    &__nav {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin-top: auto;
    }

    &__next-btn {
      width: 100%;
    }

    &__dots {
      display: flex;
      gap: 0.5rem;
      margin-top: 1.5rem;
      justify-content: center;
    }

    &__dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--slate-200);

      &--active {
        background: var(--slate-900);
      }
    }

    &__form-panel {
      padding: 3rem;
      overflow-y: auto;
      max-height: 80vh;
    }

    &__form {
      display: flex;
      flex-direction: column;
      gap: 0;
    }

    &__help {
      color: var(--slate-500);
      @include fontSize('sm');
      margin: -0.5rem 0 0;
    }

    &__row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__hint {
      color: var(--slate-500);
      margin-bottom: 0.5rem;
    }

    &__upload-label {
      @include fontSize('sm');
      font-weight: 500;
      color: var(--slate-700);
      margin-bottom: 0.25rem;
      display: block;
    }

    &__file-input {
      display: none;
    }

    &__product-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__product-card {
      cursor: pointer;
      transition: border-color 0.15s;

      &--selected {
        border-color: var(--brand-500);
        background: var(--brand-50);
      }
    }

    &__product-label {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;
      cursor: pointer;
    }

    &__product-checkbox {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }

    &__product-image {
      width: 100%;
      height: 100px;
      object-fit: cover;
      border-radius: 0.5rem;
    }

    &__product-placeholder {
      width: 100%;
      height: 100px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--slate-100);
      border-radius: 0.5rem;
      color: var(--slate-400);
    }

    &__product-title {
      font-weight: 500;
      text-align: center;
      @include fontSize('sm');
    }

    &__role-help {
      @include fontSize('sm');
      color: var(--slate-600);

      p {
        margin-bottom: 0.35rem;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    &__loading {
      display: flex;
      justify-content: center;
      padding: 2rem;
    }
  }
</style>
